import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

const Container = styled.main`
  width: calc(100% - 32px);
  max-width: 900px;
  margin: 48px auto;
  padding: clamp(20px, 5vw, 56px);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  color: #263f47;
  line-height: 1.7;
  overflow-wrap: anywhere;

  h1, h2 { color: #385963; }
  h1 { font-size: clamp(24px, 4vw, 36px); }
  h2 { font-size: 22px; margin-top: 28px; }
  a { color: #285c70; text-decoration: underline; }
  a:focus-visible { outline: 3px solid #385963; outline-offset: 4px; }
`;

const email = 'potap.pieria@gmail.com';
const requestLink = `mailto:${email}?subject=${encodeURIComponent('Olympus Riviera Greece — Account deletion request')}&body=${encodeURIComponent('Please delete my Olympus Riviera Greece account and associated personal data.\n\nApp: Android (gr.uom.olympusrivieramobileapp) / Web (please specify)\nAccount email: \n')}`;

export default function DeleteAccount() {
  const { i18n } = useTranslation();
  const isGreek = i18n.language === 'gr';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = isGreek
      ? 'Διαγραφή λογαριασμού | Olympus Riviera Greece'
      : 'Delete account | Olympus Riviera Greece';
    return () => { document.title = previousTitle; };
  }, [isGreek]);

  return (
    <Container>
      {!isGreek ? (
      <section lang="en" aria-labelledby="deletion-title">
        <h1 id="deletion-title">Olympus Riviera Greece — Account deletion</h1>
        <p>
          Request deletion of your account and associated personal data for the
          Olympus Riviera Greece Android app (<code>gr.uom.olympusrivieramobileapp</code>)
          or the Olympus Riviera web app. You can read these instructions and send
          a request without signing in or installing the app.
        </p>
        <h2>Request deletion by email</h2>
        <p>
          Email our published privacy contact at <a href={requestLink}>{email}</a> with
          the subject “Olympus Riviera Greece — Account deletion request”. Include
          the email address associated with your account and specify whether your
          request concerns the Android app, the web app, or both. Request deletion
          of your account and its associated personal data. Do not send your password.
        </p>
        <p>
          Send the request from your account email address where possible. We may
          need to verify account ownership before processing the request. Opening
          the email link does not submit a request; send the email to complete it.
        </p>
        <h2>Delete through the app or website</h2>
        <p>You can also use the existing account deletion option inside the Android app.</p>
        <ol>
          <li><Link to="/register-signIn">Sign in to the web app</Link> using the account you want to delete.</li>
          <li>Open your profile menu and select Manage Profile.</li>
          <li>Select <strong>Delete Account</strong> and confirm deletion.</li>
        </ol>
        <p>
          Already signed in as a visitor? <Link to="/profile-menu-visitor" state={{ activeComponent: 'crudProfileVisitor' }}>Open your profile and deletion controls</Link>.
          For an Android account you cannot access on the website, use the email request above.
        </p>
        <h2>Personal data and retention</h2>
        <p>
          Your request covers your account and the personal data associated with it.
          The published <Link to="/privacy-policy">Privacy Policy</Link> states a general
          personal-data retention period of five years. For details of which data
          can be erased and any retention that applies to your account, contact
          the privacy contact above as part of your deletion request.
        </p>
      </section>
      ) : (
      <section lang="el" aria-labelledby="deletion-title-el">
        <h1 id="deletion-title-el">Olympus Riviera Greece — Διαγραφή λογαριασμού</h1>
        <p>
          Ζητήστε τη διαγραφή του λογαριασμού σας και των σχετικών προσωπικών δεδομένων
          για την εφαρμογή Android Olympus Riviera Greece (<code>gr.uom.olympusrivieramobileapp</code>)
          ή τη διαδικτυακή εφαρμογή Olympus Riviera. Δεν απαιτείται σύνδεση ή εγκατάσταση
          της εφαρμογής για να διαβάσετε τις οδηγίες ή να στείλετε αίτημα.
        </p>
        <h2>Αίτημα διαγραφής μέσω email</h2>
        <p>
          Στείλτε email στον δημοσιευμένο υπεύθυνο επικοινωνίας για την προστασία
          δεδομένων, <a href={requestLink}>{email}</a>, με θέμα
          «Olympus Riviera Greece — Αίτημα διαγραφής λογαριασμού». Αναφέρετε το email
          του λογαριασμού σας και εάν το αίτημα αφορά την εφαρμογή Android, τη διαδικτυακή
          εφαρμογή ή και τις δύο. Ζητήστε τη διαγραφή του λογαριασμού και των σχετικών
          προσωπικών δεδομένων. Μην στείλετε τον κωδικό πρόσβασής σας.
        </p>
        <p>
          Εάν είναι δυνατόν, στείλτε το αίτημα από το email του λογαριασμού σας.
          Ενδέχεται να χρειαστεί επαλήθευση ότι ο λογαριασμός σάς ανήκει.
          Το άνοιγμα του συνδέσμου email δεν υποβάλλει αίτημα· πρέπει να στείλετε το email.
        </p>
        <h2>Διαγραφή μέσω της εφαρμογής ή του ιστοτόπου</h2>
        <p>Μπορείτε επίσης να χρησιμοποιήσετε την υπάρχουσα επιλογή διαγραφής λογαριασμού στην εφαρμογή Android.</p>
        <ol>
          <li><Link to="/register-signIn">Συνδεθείτε στη διαδικτυακή εφαρμογή</Link> με τον λογαριασμό που θέλετε να διαγράψετε.</li>
          <li>Ανοίξτε το μενού του προφίλ σας και επιλέξτε τη διαχείριση προφίλ.</li>
          <li>Επιλέξτε <strong>Διαγραφή Λογαριασμού</strong> και επιβεβαιώστε τη διαγραφή.</li>
        </ol>
        <p>
          Έχετε ήδη συνδεθεί ως επισκέπτης; <Link to="/profile-menu-visitor" state={{ activeComponent: 'crudProfileVisitor' }}>Ανοίξτε το προφίλ και τις επιλογές διαγραφής</Link>.
          Εάν δεν μπορείτε να αποκτήσετε πρόσβαση στον λογαριασμό Android από τον ιστότοπο,
          χρησιμοποιήστε το παραπάνω αίτημα μέσω email.
        </p>
        <h2>Προσωπικά δεδομένα και διατήρηση</h2>
        <p>
          Το αίτημά σας αφορά τον λογαριασμό και τα σχετικά προσωπικά δεδομένα.
          Η δημοσιευμένη <Link to="/privacy-policy">Πολιτική Απορρήτου</Link> αναφέρει
          γενική περίοδο διατήρησης προσωπικών δεδομένων πέντε ετών. Για τα δεδομένα
          που μπορούν να διαγραφούν και τυχόν διατήρηση που ισχύει για τον λογαριασμό
          σας, επικοινωνήστε με τον παραπάνω υπεύθυνο στο πλαίσιο του αιτήματος διαγραφής.
        </p>
      </section>
      )}
    </Container>
  );
}
